import type { Assessment, Quiz } from '@teachur/http-client';

export type UserAnswers = Record<number, number>;

interface StudentWithQuizzes {
  quizzes?: Quiz[] | null;
}

export interface StudentCourseExamProgressData {
  student?: unknown;
}

export interface ExamAttemptSummary {
  id: number | string;
  label: string;
  date: string;
  score: string;
  percentage: number;
}

export interface ExamSummary {
  attemptsCount: number;
  summaryCorrectAnswersCount: number;
  summaryTotalQuestionsCount: number;
  attempts: ExamAttemptSummary[];
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

export const extractUserAnswers = (quiz: Quiz): UserAnswers => {
  return (
    quiz.answers?.reduce<UserAnswers>((answers, answer) => {
      if (answer.assessmentId == null) return answers;

      answers[answer.assessmentId] = answer.id;

      return answers;
    }, {}) ?? {}
  );
};

export const getInitialExamQuestionState = (quiz: Quiz) => {
  const userAnswers = extractUserAnswers(quiz);

  const firstUnansweredIndex =
    quiz.assessments?.findIndex(
      (assessment: Assessment) => userAnswers[assessment.id] === undefined
    ) ?? 0;

  return {
    userAnswers,
    currentQuestionIndex: firstUnansweredIndex === -1 ? 0 : firstUnansweredIndex,
  };
};

const formatDate = (date?: string | null) => {
  if (!date) return '—';

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return '—';

  return parsedDate.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
};

const getTimestamp = (date?: string | null) => {
  if (!date) return 0;

  const timestamp = new Date(date).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getAttemptTimestamp = (attempt: Quiz) => {
  return getTimestamp(attempt.updatedAt ?? attempt.createdAt);
};

const getCorrectAnswersCount = (attempt: Quiz) => {
  return attempt.answers?.filter((answer) => answer.correct).length ?? 0;
};

const getTotalQuestionsCount = (attempt: Quiz) => {
  return attempt.assessments?.length ?? attempt.answers?.length ?? 0;
};

const getPercentage = (correctAnswersCount: number, totalQuestionsCount: number) => {
  if (!totalQuestionsCount) return 0;

  const percentage = (correctAnswersCount / totalQuestionsCount) * 100;

  return Math.min(100, Math.max(0, Math.round(percentage)));
};

const getSortedExamAttempts = (attempts?: Quiz[] | null) => {
  return [...(attempts ?? [])].sort((a, b) => getAttemptTimestamp(b) - getAttemptTimestamp(a));
};

const getExamOverallSummary = (attempts: Quiz[]) => {
  if (attempts.length === 0) {
    return {
      summaryCorrectAnswersCount: 0,
      summaryTotalQuestionsCount: 0,
    };
  }

  const oldestAttempt = attempts[attempts.length - 1];
  const summaryTotalQuestionsCount = getTotalQuestionsCount(oldestAttempt);

  const summaryCorrectAnswersCount = attempts.reduce((total, attempt) => {
    return total + getCorrectAnswersCount(attempt);
  }, 0);

  return {
    summaryCorrectAnswersCount: Math.min(summaryCorrectAnswersCount, summaryTotalQuestionsCount),
    summaryTotalQuestionsCount,
  };
};

export const getExamSummary = (
  progressData?: StudentCourseExamProgressData | null
): ExamSummary => {
  const student = progressData?.student as StudentWithQuizzes | undefined;
  const sortedAttempts = getSortedExamAttempts(student?.quizzes);

  const { summaryCorrectAnswersCount, summaryTotalQuestionsCount } =
    getExamOverallSummary(sortedAttempts);

  const attempts = sortedAttempts.map((attempt, index) => {
    const correctAnswersCount = getCorrectAnswersCount(attempt);
    const totalQuestionsCount = getTotalQuestionsCount(attempt);
    const percentage = getPercentage(correctAnswersCount, totalQuestionsCount);

    return {
      id: attempt.id,
      label: `Attempt ${sortedAttempts.length - index}`,
      date: formatDate(attempt.createdAt ?? attempt.updatedAt),
      score: `${correctAnswersCount}/${totalQuestionsCount}`,
      percentage,
    };
  });

  return {
    attemptsCount: sortedAttempts.length,
    summaryCorrectAnswersCount,
    summaryTotalQuestionsCount,
    attempts,
  };
};
